import { useContext, useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import styled from 'styled-components'

import type { components } from '../../../constants/schema'

import { clipboardCopy, toast, HttpReq } from '../../../utils'

import { StoresContext } from '../../../provider/StoreProvider'

import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { BankIcon } from '../../../components/bankIcon'
import type { BankName } from '../../../components/bankIcon/BankIcon'
import { Button } from '../../../components/button'
import { Text } from '../../../components/text'
import { NoticeContainer } from '../../../components/noticeContainer/NoticeContainer'
import { Footer, FOOTER_MIN_HEIGHT } from '../../../components/footer'
import { Header } from '../../../components/header'

import { Color } from '../../../theme/color'

import { ReactComponent as Arrow } from '../../../assets/ico/common/arrow/deposit_arrow.svg'

type DepositAccount = components['schemas']['DepositAccountDto']
type UnmaskedDepositAccount = components['schemas']['UnmaskedDepositAccountDto']
type MaskedWithdrawalAccount = components['schemas']['MaskedWithdrawalAccountDto']

export function DepositAccount() {
  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [depositAccount, setDepositAccount] = useState<DepositAccount | null>()
  const [unmaskedDepositAccount, setUnmaskedDepositAccount] = useState<UnmaskedDepositAccount | null>(null)
  const [withdrawalAccount, setWithdrawalAccount] = useState<MaskedWithdrawalAccount | null>(null)

  useEffect(() => {
    setShowTab(false)
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const depositAccountResponse = await HttpReq<DepositAccount>({
        method: 'GET',
        url: '/user/current/depositAccount',
      })
      const unmaskedDepositAccountResponse = await HttpReq<UnmaskedDepositAccount>({
        method: 'GET',
        url: '/user/current/depositAccount/unmask',
      })
      const withdrawalAccountResponse = await HttpReq<MaskedWithdrawalAccount>({
        method: 'GET',
        url: '/user/current/withdrawalAccount',
      })

      setDepositAccount(depositAccountResponse)
      setUnmaskedDepositAccount(unmaskedDepositAccountResponse)
      setWithdrawalAccount(withdrawalAccountResponse)
      setIsLoading(false)
    } catch (error) {
      if (isAxiosError(error)) {
        toast(error.response?.data.message, error, false)
      }
      setInvalidAccess(true)
    }
  }

  if (isLoading) {
    return <></>
  }

  const copyAccount = () => {
    if (!unmaskedDepositAccount) return
    const { account } = unmaskedDepositAccount
    clipboardCopy(account, { message: '✓ 예치금 계좌 번호 복사가 완료되었어요', isFooter: false })
  }

  return (
    <Wrap>
      <Header type="SUB" title="예치금 충전 계좌" />
      <StyledContent backgroundColor="gray_1">
        <DepositAccountBox>
          <Text size={24} weight={600} lineHeight={34} color="text_white" block={true}>
            내 예치금 계좌
          </Text>
          <BankInfoBox>
            <BankInfo>
              <BankIcon name="NH농협은행" size={46} />
              <Text size={18} weight={600} color="text_white">
                NH농협은행
              </Text>
            </BankInfo>
            <Button type="primary" size="compact" onClick={copyAccount}>
              계좌 번호 복사
            </Button>
          </BankInfoBox>
          <AccountInfo>
            <RowBetween>
              <Text size={14} weight={600} color="text_white">
                계좌번호
              </Text>
              <Text size={14} color="text_white">
                {depositAccount?.account}
              </Text>
            </RowBetween>
            <RowBetween>
              <Text size={14} weight={600} color="text_white">
                예금주
              </Text>
              <Text size={16} color="text_white">
                {depositAccount?.bankOwner}
              </Text>
            </RowBetween>
          </AccountInfo>
        </DepositAccountBox>

        <WithdrawAccountBox>
          <ArrowBox>
            <Arrow />
          </ArrowBox>
          <WithdrawDescription>
            <Text size={16} weight={500} lineHeight={24} block={true}>
              등록한<WithdrawBadge>{withdrawalAccount?.bankName}</WithdrawBadge>계좌의 앱에서
            </Text>
            <Text size={16} weight={500} lineHeight={24} block={true}>
              내 예치금 계좌로 송금해주세요.
            </Text>
          </WithdrawDescription>
          <WithdrawAccount>
            <WithdrawTitle>등록한 출금계좌</WithdrawTitle>
            <BankInfoBox>
              <BankInfo>
                <BankIcon name={withdrawalAccount?.bankName as BankName} size={46} />
                <Text size={18} weight={600} color="text_primary">
                  {withdrawalAccount?.bankName}
                </Text>
              </BankInfo>
            </BankInfoBox>
            <AccountInfo>
              <RowBetween>
                <Text size={14} weight={600} color="text_secondary">
                  계좌번호
                </Text>
                <Text size={14} color="text_secondary">
                  {withdrawalAccount?.account}
                </Text>
              </RowBetween>
              <RowBetween>
                <Text size={14} weight={600} color="text_secondary">
                  예금주
                </Text>
                <Text size={16} color="text_secondary">
                  {withdrawalAccount?.bankOwner}
                </Text>
              </RowBetween>
            </AccountInfo>
          </WithdrawAccount>
        </WithdrawAccountBox>

        <NoticeContainer
          title="안내사항"
          list={[
            '최소 충전 가능 금액은 10,000원이에요.',
            '예치금 출금 시 수수료는 1,000원이 부과돼요.',
            '예치금 충전은 은행 정산 상태에 따라 최대 10분 정도 소요될 수 있어요.',
            '본 계좌 번호가 아닌 평생 계좌 번호(휴대폰, 사업자 번호 등)는 등록이 불가능해요.',
            '같은 계좌이지만, 계좌 번호가 변경된 경우, 변경된 계좌 번호를 입력해 주세요.',
            '반드시, 등록한 출금 계좌에서 충전 계좌로 입금해 주세요.',
            '오픈 뱅킹(카카오뱅크, 토스 등), 간편 결제(네이버페이, 카카오페이 등)를 통한 입금은 금융사 정책으로 인해 제한돼요.',
          ]}
          $backgroundColor="gray_1"
        />
      </StyledContent>
      <Footer>
        <Button type="primary" size="large" block={true} onClick={copyAccount}>
          농협 예치금 계좌번호 복사
        </Button>
      </Footer>
    </Wrap>
  )
}

const StyledContent = styled(Content)`
  padding-bottom: ${FOOTER_MIN_HEIGHT}px;
`

const RowBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const DepositAccountBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  padding: 40px 24px 32px;
  background-color: ${Color.white};
  &::before {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    border-bottom-right-radius: 20px;
    border-bottom-left-radius: 20px;
    background-color: ${Color.primary_5};
  }
  > * {
    z-index: 0;
  }
`

const BankInfoBox = styled(RowBetween)`
  margin-bottom: 24px;
`

const BankInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const AccountInfo = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
`

const WithdrawAccountBox = styled.div`
  position: relative;
  padding: 0 16px 40px;
  background-color: ${Color.white};
`

const ArrowBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -14px;
  left: 50%;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${Color.white};
  transform: translateX(-50%);
`

const WithdrawDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 30px 0 24px;
  border-bottom: 1px solid #eaeaea;
  text-align: center;
`

const WithdrawAccount = styled.div`
  padding: 0 8px;
`

const WithdrawBadge = styled.span`
  margin: 0 4px;
  padding: 3px 6px;
  background-color: ${Color.primary_2};
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  color: ${Color.primary_5};
`

const WithdrawTitle = styled.p`
  margin: 24px 0 16px;
  font-size: 16px;
  font-weight: 700;
  color: ${Color.text_primary};
`
